import { useCallback, useEffect, useMemo, useState } from "react";
import { KeyRound, RefreshCw, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { LengthUnit, NotificationPreferences, Passkey, SessionInfo } from "../types/models";
import { Button } from "../components/Button";
import { EmptyState } from "../components/EmptyState";
import { InputField, SelectField } from "../components/Field";
import { IconButton } from "../components/IconButton";
import { PageHeader } from "../components/PageHeader";
import { QRCodeImage } from "../components/QRCodeImage";
import { SettingsRow } from "../components/SettingsRow";
import { Skeleton } from "../components/Skeleton";
import { Tabs } from "../components/Tabs";
import { useAuth } from "../app/AuthProvider";
import { usePreferences } from "../app/PreferencesProvider";
import { useI18n } from "../i18n/I18nProvider";
import { useToast, type ToastOptions } from "../components/Toast";
import { ApiRequestError } from "../services/api";
import {
  changePassword,
  deleteOwnAccount,
  deletePasskey,
  generateRecoveryCodes,
  listPasskeys,
  listSessions,
  logoutAll,
  registerPasskeyWithBrowser,
  revokeSession,
  setupTotp,
  verifyTotp
} from "../services/auth";
import { completeOnboarding, updateProfile, updateSettings } from "../services/profile";
import { getNotificationPreferences, sendTestNotification, updateNotificationPreferences } from "../services/notifications";
import { setHydrationGoal } from "../services/water";
import { subscribeToWebPush } from "../lib/webPush";
import { detectBrowserTimezone } from "../lib/timezone";
import { buildHydrationPreview } from "../lib/hydrationPreview";
import { formatVolume, lengthFromCm } from "../lib/units";
import { isWebAuthnSupported } from "../lib/webauthn";

type SettingsTab = "profile" | "units" | "notifications" | "security" | "data";

export function SettingsPage() {
  const { t, translateError } = useI18n();
  const { user, refreshSession, logout } = useAuth();
  const navigate = useNavigate();
  const { preferences, updatePreferences } = usePreferences();
  const { showToast } = useToast();
  const [tab, setTab] = useState<SettingsTab>("profile");
  const [displayName, setDisplayName] = useState(user?.display_name ?? "");
  const [profileHeight, setProfileHeight] = useState("");
  const [profileBirthYear, setProfileBirthYear] = useState("");
  const [profileReference, setProfileReference] = useState<"female" | "male">("female");
  const [goalMl, setGoalMl] = useState("2500");
  const [notificationPrefs, setNotificationPrefs] = useState<NotificationPreferences | null>(null);
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [loadingSecurity, setLoadingSecurity] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [totpSetup, setTotpSetup] = useState<Awaited<ReturnType<typeof setupTotp>> | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const passkeySupported = isWebAuthnSupported();
  const profileHeightNumber = readPositiveNumber(profileHeight);
  const profileBirthYearNumber = readBirthYear(profileBirthYear);
  const profilePreview = useMemo(
    () =>
      buildHydrationPreview({
        height: profileHeightNumber,
        heightUnit: preferences.lengthUnit,
        birthYear: profileBirthYearNumber,
        hydrationReference: profileReference,
        weightUnit: preferences.weightUnit
      }),
    [preferences.lengthUnit, preferences.weightUnit, profileBirthYearNumber, profileHeightNumber, profileReference]
  );

  const refreshSettings = useCallback(async () => {
    const [prefs, nextSessions, nextPasskeys] = await Promise.all([
      getNotificationPreferences().catch(() => null),
      listSessions().catch(() => []),
      listPasskeys().catch(() => [])
    ]);
    setNotificationPrefs(prefs);
    setSessions(nextSessions);
    setPasskeys(nextPasskeys);
  }, []);

  useEffect(() => {
    void refreshSettings();
  }, [refreshSettings]);

  useEffect(() => {
    setDisplayName(user?.display_name ?? "");
    setProfileHeight(formatLengthInput(user?.height_cm, preferences.lengthUnit));
    setProfileBirthYear(formatBirthYearInput(user?.date_of_birth));
    setProfileReference(readHydrationReference(user?.sex_for_hydration_formula));
  }, [preferences.lengthUnit, user?.date_of_birth, user?.display_name, user?.height_cm, user?.sex_for_hydration_formula]);

  const run = async (action: () => Promise<void>) => {
    try {
      await action();
      showToast({ title: t("common.done") });
    } catch (error) {
      handleError(error, translateError, showToast, {
        passkeyCancelledMessage: t("auth.passkeyCancelled"),
        passkeyUnsupportedMessage: t("auth.passkeyUnsupported"),
        profileIncompleteMessage: t("onboarding.completeRequired"),
        publicKeyMissingMessage: t("settings.publicKeyMissing")
      });
    }
  };

  return (
    <>
      <PageHeader
        title={t("settings.title")}
        actions={
          <IconButton label={t("common.refresh")} onClick={() => void refreshSettings()}>
            <RefreshCw size={18} aria-hidden="true" />
          </IconButton>
        }
      />
      <div className="stack">
        <Tabs
          value={tab}
          onChange={setTab}
          items={[
            { value: "profile", label: t("settings.profile") },
            { value: "units", label: t("settings.units") },
            { value: "notifications", label: t("settings.notifications") },
            { value: "security", label: t("settings.security") },
            { value: "data", label: t("settings.dataExport") }
          ]}
        />

        {tab === "profile" ? (
          <section className="panel stack">
            <h2>{t("settings.profile")}</h2>
            <InputField label={t("auth.displayName")} value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
            <SelectField
              label={t("settings.language")}
              value={preferences.locale}
              onChange={(event) => updatePreferences({ locale: event.target.value as "en" | "de" })}
              options={[
                { label: "English", value: "en" },
                { label: "Deutsch", value: "de" }
              ]}
            />
            <SelectField
              label={t("onboarding.timezone")}
              value={preferences.timezoneMode}
              onChange={(event) => updatePreferences({ timezoneMode: event.target.value as "detected" | "fixed" })}
              options={[
                { label: t("onboarding.autoTimezone"), value: "detected" },
                { label: t("onboarding.fixedTimezone"), value: "fixed" }
              ]}
            />
            {preferences.timezoneMode === "fixed" ? (
              <InputField
                label={t("onboarding.fixedTimezoneLabel")}
                value={preferences.fixedTimezone}
                onChange={(event) => updatePreferences({ fixedTimezone: event.target.value })}
              />
            ) : (
              <p className="field__hint">{detectBrowserTimezone()}</p>
            )}
            <div className="settings-profile-details">
              <h3>{t("settings.hydrationDetails")}</h3>
              <div className="grid-two">
                <UnitNumberField
                  id="settings-height"
                  label={t("onboarding.height")}
                  min={1}
                  unit={preferences.lengthUnit}
                  unitLabel={t("onboarding.lengthUnit")}
                  value={profileHeight}
                  onUnitChange={(unit) => updatePreferences({ lengthUnit: unit as LengthUnit })}
                  onValueChange={setProfileHeight}
                  options={[
                    { label: "cm", value: "cm" },
                    { label: "inch", value: "inch" }
                  ]}
                  required
                />
                <InputField
                  label={t("onboarding.birthYear")}
                  type="number"
                  min={1900}
                  max={new Date().getFullYear()}
                  value={profileBirthYear}
                  onChange={(event) => setProfileBirthYear(event.target.value)}
                  required
                />
              </div>
              <div className="field">
                <span className="field__label">{t("onboarding.reference")}</span>
                <div className="segmented">
                  <button type="button" aria-pressed={profileReference === "female"} onClick={() => setProfileReference("female")}>
                    {t("onboarding.female")}
                  </button>
                  <button type="button" aria-pressed={profileReference === "male"} onClick={() => setProfileReference("male")}>
                    {t("onboarding.male")}
                  </button>
                </div>
              </div>
              <p className="field__hint">
                {t("settings.estimatedGoal")}: {profilePreview ? formatVolume(profilePreview.goalMl, preferences.volumeUnit) : "-"}
              </p>
            </div>
            <Button
              onClick={() =>
                void run(async () => {
                  if (!profileHeightNumber || !profileBirthYearNumber || !profilePreview) {
                    throw new Error("PROFILE_DETAILS_INCOMPLETE");
                  }
                  await completeOnboarding(
                    {
                      height: profileHeightNumber,
                      birthYear: profileBirthYearNumber,
                      hydrationReference: profileReference,
                      customGoalMl: profilePreview.goalMl
                    },
                    preferences
                  );
                  await updateProfile({
                    display_name: displayName,
                    locale: preferences.locale,
                    timezone_mode: preferences.timezoneMode,
                    timezone: preferences.timezoneMode === "fixed" ? preferences.fixedTimezone : detectBrowserTimezone()
                  });
                  await refreshSession({ requireProfile: true });
                })
              }
            >
              {t("common.save")}
            </Button>
          </section>
        ) : null}

        {tab === "units" ? (
          <section className="panel stack">
            <h2>{t("settings.units")}</h2>
            <SelectField
              label={t("onboarding.volumeUnit")}
              value={preferences.volumeUnit}
              onChange={(event) => updatePreferences({ volumeUnit: event.target.value as "ml" | "oz" })}
              options={[
                { label: "ml", value: "ml" },
                { label: "oz", value: "oz" }
              ]}
            />
            <SelectField
              label={t("onboarding.lengthUnit")}
              value={preferences.lengthUnit}
              onChange={(event) => updatePreferences({ lengthUnit: event.target.value as "cm" | "inch" })}
              options={[
                { label: "cm", value: "cm" },
                { label: "inch", value: "inch" }
              ]}
            />
            <SelectField
              label={t("onboarding.weightUnit")}
              value={preferences.weightUnit}
              onChange={(event) => updatePreferences({ weightUnit: event.target.value as "kg" | "lb" })}
              options={[
                { label: "kg", value: "kg" },
                { label: "lb", value: "lb" }
              ]}
            />
            <SelectField
              label={t("settings.theme")}
              value={preferences.theme}
              onChange={(event) => updatePreferences({ theme: event.target.value as "system" | "light" | "dark" })}
              options={[
                { label: t("settings.themeSystem"), value: "system" },
                { label: t("settings.themeLight"), value: "light" },
                { label: t("settings.themeDark"), value: "dark" }
              ]}
            />
            <InputField label={t("settings.goal")} type="number" min={250} max={10000} value={goalMl} onChange={(event) => setGoalMl(event.target.value)} />
            <Button onClick={() => void run(async () => {
              await setHydrationGoal(Number(goalMl));
            })}>
              {t("common.save")}
            </Button>
            <SettingsRow
              title={t("settings.showStreaks")}
              control={
                <input
                  type="checkbox"
                  checked={preferences.showStreaks}
                  onChange={(event) => updatePreferences({ showStreaks: event.target.checked })}
                  aria-label={t("settings.showStreaks")}
                />
              }
            />
            <Button
              variant="secondary"
              onClick={() =>
                void run(async () => {
                  await updateSettings({
                    default_cup_volume_ml: 250,
                    default_cup_icon: "cup",
                    reminder_enabled: notificationPrefs?.reminders_enabled,
                    reminder_interval_minutes: 120
                  });
                })
              }
            >
              {t("common.save")}
            </Button>
          </section>
        ) : null}

        {tab === "notifications" ? (
          <section className="panel stack">
            <h2>{t("settings.notifications")}</h2>
            {notificationPrefs ? (
              <>
                <SettingsRow
                  title={t("settings.emailNotifications")}
                  control={
                    <input
                      type="checkbox"
                      checked={notificationPrefs.email_enabled ?? false}
                      onChange={(event) =>
                        void run(async () => {
                          const next = await updateNotificationPreferences({ email_enabled: event.target.checked });
                          setNotificationPrefs(next);
                        })
                      }
                      aria-label={t("settings.emailNotifications")}
                    />
                  }
                />
                <SettingsRow
                  title={t("settings.webPush")}
                  detail={t("settings.publicKeyMissing")}
                  control={
                    <input
                      type="checkbox"
                      checked={notificationPrefs.web_push_enabled ?? false}
                      onChange={(event) =>
                        void run(async () => {
                          if (event.target.checked) {
                            await subscribeToWebPush();
                          }
                          const next = await updateNotificationPreferences({ web_push_enabled: event.target.checked });
                          setNotificationPrefs(next);
                        })
                      }
                      aria-label={t("settings.webPush")}
                    />
                  }
                />
                <SettingsRow
                  title={t("settings.reminders")}
                  control={
                    <input
                      type="checkbox"
                      checked={notificationPrefs.reminders_enabled ?? false}
                      onChange={(event) =>
                        void run(async () => {
                          const next = await updateNotificationPreferences({ reminders_enabled: event.target.checked });
                          setNotificationPrefs(next);
                        })
                      }
                      aria-label={t("settings.reminders")}
                    />
                  }
                />
                <div className="grid-two">
                  <Button variant="secondary" onClick={() => void run(async () => sendTestNotification("email"))}>
                    {t("settings.sendTest")} email
                  </Button>
                  <Button variant="secondary" onClick={() => void run(async () => sendTestNotification("web_push"))}>
                    {t("settings.sendTest")} web push
                  </Button>
                </div>
              </>
            ) : (
              <EmptyState title={t("common.empty")} />
            )}
          </section>
        ) : null}

        {tab === "security" ? (
          <section className="panel stack">
            <h2>{t("settings.security")}</h2>
            {loadingSecurity ? <Skeleton rows={3} /> : null}
            <div className="grid-two">
              <InputField label={t("auth.currentPassword")} type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} />
              <InputField label={t("auth.newPassword")} type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} />
            </div>
            <Button
              onClick={() =>
                void run(async () => {
                  await changePassword({ current_password: currentPassword, new_password: newPassword });
                  setCurrentPassword("");
                  setNewPassword("");
                })
              }
            >
              {t("common.save")}
            </Button>
            <div className="grid-two">
              <Button
                variant="secondary"
                icon={<KeyRound size={16} aria-hidden="true" />}
                onClick={() =>
                  void run(async () => {
                    setLoadingSecurity(true);
                    setTotpSetup(await setupTotp());
                    setLoadingSecurity(false);
                  })
                }
              >
                {t("settings.setupTotp")}
              </Button>
              <Button variant="secondary" onClick={() => void run(async () => setRecoveryCodes(await generateRecoveryCodes()))}>
                {t("settings.generateCodes")}
              </Button>
            </div>
            {totpSetup ? (
              <div className="panel stack">
                <p className="field__hint">{t("settings.totpScan")}</p>
                <QRCodeImage value={totpSetup.otpauth_uri ?? totpSetup.otpauth_url ?? ""} label={t("settings.totpQr")} />
                <div className="manual-code">
                  <span>{t("settings.totpManual")}</span>
                  <code>{totpSetup.manual_code ?? totpSetup.secret ?? ""}</code>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      const code = totpSetup.manual_code ?? totpSetup.secret ?? "";
                      if (code) {
                        void navigator.clipboard?.writeText(code.replace(/\s/g, ""));
                      }
                    }}
                  >
                    {t("common.copy")}
                  </Button>
                </div>
                <InputField label={t("auth.totpCode")} value={totpCode} onChange={(event) => setTotpCode(event.target.value)} />
                <Button onClick={() => void run(async () => verifyTotp({ code: totpCode }))}>{t("settings.verifyTotp")}</Button>
              </div>
            ) : null}
            {recoveryCodes.length ? (
              <pre className="panel">{recoveryCodes.join("\n")}</pre>
            ) : null}
            <h3>{t("settings.passkeys")}</h3>
            <Button
              onClick={() =>
                void run(async () => {
                  setLoadingSecurity(true);
                  try {
                    await registerPasskeyWithBrowser();
                    setPasskeys(await listPasskeys());
                  } finally {
                    setLoadingSecurity(false);
                  }
                })
              }
              disabled={!passkeySupported || loadingSecurity}
            >
              {t("settings.addPasskey")}
            </Button>
            {!passkeySupported ? <p className="field__hint">{t("auth.passkeyUnsupported")}</p> : null}
            {passkeys.length ? (
              <div className="list">
                {passkeys.map((passkey) => (
                  <div className="list-item" key={passkey.id}>
                    <div className="list-item__main">
                      <strong>{passkey.friendly_name ?? passkey.device_hint ?? t("settings.passkey")}</strong>
                      <span className="list-item__meta">{formatPasskeyMeta(passkey, t)}</span>
                    </div>
                    <IconButton
                      label={t("common.delete")}
                      variant="danger"
                      onClick={() =>
                        void run(async () => {
                          await deletePasskey(passkey.id);
                          setPasskeys((current) => current.filter((item) => item.id !== passkey.id));
                        })
                      }
                    >
                      <Trash2 size={16} aria-hidden="true" />
                    </IconButton>
                  </div>
                ))}
              </div>
            ) : null}
            <h3>{t("settings.sessions")}</h3>
            {sessions.length ? (
              <div className="list">
                {sessions.map((session) => (
                  <div className="list-item" key={session.id}>
                    <div className="list-item__main">
                      <strong>
                        {formatSessionTitle(session)}
                        {session.current ? <span className="badge">{t("settings.currentSession")}</span> : null}
                      </strong>
                      <span className="list-item__meta">{formatSessionMeta(session, t)}</span>
                    </div>
                    <Button
                      variant={session.current ? "danger" : "secondary"}
                      size="sm"
                      disabled={Boolean(session.revoked_at)}
                      onClick={() =>
                        void run(async () => {
                          await revokeSession(session.id);
                          setSessions((current) => current.filter((item) => item.id !== session.id));
                          if (session.current) {
                            await refreshSession();
                            void navigate("/auth", { replace: true });
                          }
                        })
                      }
                    >
                      {session.current ? t("settings.revokeCurrentSession") : t("settings.revokeSession")}
                    </Button>
                  </div>
                ))}
              </div>
            ) : null}
            <Button
              variant="danger"
              onClick={() =>
                void run(async () => {
                  await logoutAll();
                  await refreshSession();
                  void navigate("/auth", { replace: true });
                })
              }
            >
              {t("auth.logoutAll")}
            </Button>
          </section>
        ) : null}

        {tab === "data" ? (
          <section className="panel stack">
            <h2>{t("settings.dataExport")}</h2>
            <Button variant="secondary" onClick={() => window.location.assign("/reports")}>
              {t("reports.create")}
            </Button>
            <SettingsRow
              title={t("settings.accountDeletion")}
              detail={t("settings.accountDeletionDetail")}
              control={
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() =>
                    void run(async () => {
                      if (!window.confirm(t("settings.accountDeletionConfirm"))) {
                        return;
                      }
                      await deleteOwnAccount();
                      await refreshSession();
                      void navigate("/auth", { replace: true });
                    })
                  }
                >
                  {t("settings.deleteAccount")}
                </Button>
              }
            />
          </section>
        ) : null}

        <section className="panel stack">
          <h2>{t("settings.account")}</h2>
          <Button
            variant="danger"
            onClick={() =>
              void run(async () => {
                await logout();
                void navigate("/auth", { replace: true });
              })
            }
          >
            {t("auth.logout")}
          </Button>
        </section>
      </div>
    </>
  );
}

type UnitNumberFieldProps = {
  id: string;
  label: string;
  value: string;
  unit: string;
  unitLabel: string;
  min?: number;
  required?: boolean;
  options: Array<{ label: string; value: string }>;
  onValueChange: (value: string) => void;
  onUnitChange: (value: string) => void;
};

function UnitNumberField({ id, label, value, unit, unitLabel, min, required, options, onValueChange, onUnitChange }: UnitNumberFieldProps) {
  return (
    <label className="field" htmlFor={id}>
      <span className="field__label">{label}</span>
      <span className="unit-input">
        <input
          className="input unit-input__value"
          id={id}
          min={min}
          onChange={(event) => onValueChange(event.target.value)}
          required={required}
          type="number"
          value={value}
        />
        <select aria-label={unitLabel} className="input select unit-input__unit" onChange={(event) => onUnitChange(event.target.value)} value={unit}>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </span>
    </label>
  );
}

function readPositiveNumber(value: string): number | null {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
}

function readBirthYear(value: string): number | null {
  const number = Number(value);
  const currentYear = new Date().getFullYear();
  return Number.isInteger(number) && number >= 1900 && number <= currentYear ? number : null;
}

function formatLengthInput(valueCm: number | null | undefined, unit: "cm" | "inch"): string {
  return valueCm ? formatNumberInput(lengthFromCm(valueCm, unit)) : "";
}

function formatBirthYearInput(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  const year = new Date(value).getUTCFullYear();
  return Number.isFinite(year) ? String(year) : "";
}

function formatNumberInput(value: number): string {
  return String(Math.round(value * 10) / 10);
}

function readHydrationReference(value: string | null | undefined): "female" | "male" {
  return value === "male" ? "male" : "female";
}

function formatPasskeyMeta(passkey: Passkey, t: (key: string) => string): string {
  const parts = [
    passkey.device_hint,
    passkey.transports?.length ? passkey.transports.join(", ") : "",
    passkey.created_at ? `${t("settings.created")}: ${formatDateTime(passkey.created_at)}` : "",
    passkey.last_used_at ? `${t("settings.lastUsed")}: ${formatDateTime(passkey.last_used_at)}` : t("settings.neverUsed")
  ].filter(Boolean);
  return parts.join(" / ");
}

function formatSessionTitle(session: SessionInfo): string {
  const device = session.device_type && session.device_type !== "unknown" ? capitalize(session.device_type) : "";
  const os = [session.os, session.os_version].filter(Boolean).join(" ");
  const browser = [session.browser, session.browser_version].filter(Boolean).join(" ");
  return [device, os, browser].filter(Boolean).join(" / ") || session.id;
}

function formatSessionMeta(session: SessionInfo, t: (key: string) => string): string {
  const lastActive = session.last_seen_at ?? session.created_at;
  const ip = readableSessionIp(session.ip);
  const parts = [
    ip ? `${t("settings.ip")}: ${ip}` : session.ip ? `${t("settings.ip")}: ${t("settings.ipUnavailable")}` : "",
    session.location ? `${t("settings.location")}: ${session.location}` : "",
    lastActive ? `${t("settings.lastActive")}: ${formatDateTime(lastActive)}` : "",
    session.expires_at ? `${t("settings.expires")}: ${formatDateTime(session.expires_at)}` : "",
    session.revoked_at ? t("settings.sessionRevoked") : ""
  ].filter(Boolean);
  return parts.join(" / ");
}

function readableSessionIp(value: string | undefined): string | undefined {
  if (!value) {
    return undefined;
  }
  const normalized = value.trim();
  if (/^sha/i.test(normalized) || /^[a-f0-9]{64}$/i.test(normalized)) {
    return undefined;
  }
  return normalized;
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function capitalize(value: string): string {
  return value ? `${value.slice(0, 1).toUpperCase()}${value.slice(1)}` : value;
}

function handleError(
  error: unknown,
  translateError: (code: string) => string,
  showToast: (toast: ToastOptions) => string,
  messages: { passkeyCancelledMessage: string; passkeyUnsupportedMessage: string; profileIncompleteMessage: string; publicKeyMissingMessage: string }
): void {
  if (error instanceof ApiRequestError) {
    showToast({ title: translateError(error.code), tone: "error" });
  } else if (error instanceof Error && error.message === "PROFILE_DETAILS_INCOMPLETE") {
    showToast({ title: messages.profileIncompleteMessage, tone: "error" });
  } else if (error instanceof Error && error.message === "PASSKEY_UNSUPPORTED") {
    showToast({ title: messages.passkeyUnsupportedMessage, tone: "error" });
  } else if (error instanceof Error && error.message === "PASSKEY_CANCELLED") {
    showToast({ title: messages.passkeyCancelledMessage, tone: "error" });
  } else if (error instanceof Error && error.message === "WEB_PUSH_PUBLIC_KEY_MISSING") {
    showToast({ title: messages.publicKeyMissingMessage, tone: "error" });
  } else {
    showToast({ title: translateError("INTERNAL_ERROR"), tone: "error" });
  }
}
